import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useEventTracking } from '../../hooks/useEventTracking';
import glassTheme from '../../styles/glassTheme';

const PageContainer = styled.div`
  min-height: 100vh;
  ${glassTheme.gridBackground}
  padding: 2rem;
`;

const Header = styled.div`
  max-width: 1200px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.secondary};
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const CategorySection = styled.div`
  margin-bottom: 3rem;
`;

const CategoryHeader = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  ${glassTheme.glass.light}
  border-left: 4px solid ${props => props.$color};
`;

const CategoryIcon = styled.span`
  font-size: 2rem;
  margin-right: 1rem;
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const CategoryTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin: 0;
`;

const CategoryDescription = styled.p`
  font-size: 0.9rem;
  color: ${glassTheme.colors.text.secondary};
  margin: 0.25rem 0 0;
`;

const TopicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
`;

const TopicCard = styled.div`
  ${glassTheme.glass.light}
  padding: 1.5rem;
  transition: all ${glassTheme.transition.base};
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    border-color: ${glassTheme.colors.borderHover};
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
  }
`;

const TopicHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const TopicTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  background: ${glassTheme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
  flex: 1;
  line-height: 1.4;
`;

const TopicDescription = styled.p`
  font-size: 0.95rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const TopicMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.muted};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PrerequisitesTag = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${glassTheme.colors.border};
  font-size: 0.85rem;
  color: ${glassTheme.colors.status.warning};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.secondary};
`;

const ErrorMessage = styled.div`
  ${glassTheme.glass.heavy}
  color: ${glassTheme.colors.status.error};
  padding: 1rem 1.5rem;
  margin: 2rem auto;
  max-width: 600px;
  text-align: center;
`;

interface Category {
  _id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  order: number;
}

interface Topic {
  _id: string;
  title: string;
  slug: string;
  description: string;
  category: Category;
  order: number;
  estimatedTime: number;
  prerequisites: Array<{ _id: string; title: string; slug: string }>;
}

interface CategoryWithTopics {
  category: Category;
  topics: Topic[];
}

export const TopicsList: React.FC = () => {
  const [categoriesWithTopics, setCategoriesWithTopics] = useState<CategoryWithTopics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { trackTopicStart } = useEventTracking();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [categoriesRes, topicsRes] = await Promise.all([
          axios.get('/api/categories'),
          axios.get('/api/topics?published=true')
        ]);

        const categories: Category[] = categoriesRes.data.data.categories;
        const topics: Topic[] = topicsRes.data.data.topics;

        // Group topics by category
        const grouped = categories.map(category => ({
          category,
          topics: topics.filter(topic => topic.category._id === category._id)
        })).filter(group => group.topics.length > 0);

        setCategoriesWithTopics(grouped);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.message || '加载数据失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

  }, []);

  const handleTopicClick = (topic: Topic) => {
    // Track topic start
    trackTopicStart({
      topicId: topic._id,
      topicSlug: topic.slug,
      topicTitle: topic.title,
      category: topic.category.name
    });

    navigate(`/learning/topics/${topic.slug}`);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>加载中...</LoadingSpinner>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <Title>统计学学习主题</Title>
        <Subtitle>探索心理学和行为科学中的统计方法</Subtitle>
      </Header>

      <ContentContainer>
        {categoriesWithTopics.map(({ category, topics }) => (
          <CategorySection key={category._id}>
            <CategoryHeader $color={category.color}>
              <CategoryIcon>{category.icon}</CategoryIcon>
              <CategoryInfo>
                <CategoryTitle>{category.name}</CategoryTitle>
                <CategoryDescription>{category.description}</CategoryDescription>
              </CategoryInfo>
            </CategoryHeader>

            <TopicsGrid>
              {topics.map(topic => (
                <TopicCard
                  key={topic._id}
                  onClick={() => handleTopicClick(topic)}
                >
                  <TopicHeader>
                    <TopicTitle>{topic.title}</TopicTitle>
                  </TopicHeader>

                  <TopicDescription>{topic.description}</TopicDescription>

                  <TopicMeta>
                    <MetaItem>
                      <span>⏱</span>
                      <span>{topic.estimatedTime} 分钟</span>
                    </MetaItem>
                    {topic.prerequisites.length > 0 && (
                      <MetaItem>
                        <span>📚</span>
                        <span>{topic.prerequisites.length} 个前置主题</span>
                      </MetaItem>
                    )}
                  </TopicMeta>

                  {topic.prerequisites.length > 0 && (
                    <PrerequisitesTag>
                      建议先学习：{topic.prerequisites.map(p => p.title).join('、')}
                    </PrerequisitesTag>
                  )}
                </TopicCard>
              ))}
            </TopicsGrid>
          </CategorySection>
        ))}
      </ContentContainer>
    </PageContainer>
  );
};

export default TopicsList;
